import { isPresent } from 'utils/isPresent';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import withStyles from 'housecall-ui/dist/DesignSystem/Styles/withStyles';
import FormTextField from '../../Common/Form/FormTextField.js.jsx';
import FormPhoneNumberField from 'components/Common/Inputs/FormControls/FormPhoneNumberField.js';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';
import { FieldArray, getFormValues, change } from 'redux-form';
import Hidden from 'housecall-ui/dist/DesignSystem/Layout/Hidden/Hidden';
import IconButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/IconButton';
import CancelIcon from 'housecall-ui/dist/IconsAndImages/Icons/Cancel';
import ConsumerAppChip from '../../Common/ConsumerAppChip.jsx';
import { addAdditionalEmails, addAdditionalPhones } from './Redux/Actions';
import FaceIcon from 'housecall-ui/dist/IconsAndImages/Icons/Face';
import SectionTitle from './SectionTitle';
import CustomerSubcontractCell from './CustomerSubcontractCell.jsx';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import RadioGroup from 'housecall-ui/dist/DesignSystem/Inputs/RadioButtons/RadioGroup';
import FormControlLabel from 'housecall-ui/dist/DesignSystem/Inputs/FormControls/FormControlLabel';
import Radio from 'housecall-ui/dist/DesignSystem/Inputs/RadioButtons/Radio';
import { isBlank } from 'utils/isBlank';

const styles = (theme) => ({
  addMoreButton: {
    marginBottom: 10,
  },
  deleteRowStyle: {
    backgroundColor: 'transparent !important',
    width: '1px',
  },
  deleteRowIcon: {
    [theme.breakpoints.down('xs')]: {
      position: 'relative',
      right: 10,
      color: theme.palette.buttonBackgroundColor,
    },
    [theme.breakpoints.up('sm')]: {
      color: theme.palette.buttonBackgroundColor,
      '&:hover': {
        fill: theme.palette.error[500],
      },
    },
  },
  emptyPaddingCell: {
    padding: '0 !important',
  },
  radioButton: {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  companyPosition: {
    position: 'relative',
    left: 77,
  },
});

function determineDefaultDisplayName(companyInfo) {
  if (isBlank(companyInfo)) {
    return '';
  }

  if (isPresent(companyInfo.company)) {
    return companyInfo.company;
  } else if (isPresent(companyInfo.first_name) && isPresent(companyInfo.last_name)) {
    return companyInfo.first_name + ' ' + companyInfo.last_name;
  } else if (isPresent(companyInfo.first_name)) {
    return companyInfo.first_name;
  } else if (isPresent(companyInfo.last_name)) {
    return companyInfo.last_name;
  } else if (isPresent(companyInfo.email)) {
    return companyInfo.email;
  } else {
    return '';
  }
}

function shouldUseDefaultDisplayName(preProps, newProps) {
  return (
    preProps.displayName === preProps.defaultDisplayName && preProps.defaultDisplayName !== newProps.defaultDisplayName
  );
}

@connect(
  (state, props) => ({
    defaultDisplayName: determineDefaultDisplayName(getFormValues(props.formNamespace)(state)),
    customerKind: (getFormValues(props.formNamespace)(state) || {}).kind || 'homeowner',
    displayName: (getFormValues(props.formNamespace)(state) || {}).display_name || '',
    housecallUser: (getFormValues(props.formNamespace)(state) || {}).is_housecall || false,
  }),
  (dispatch, props) => ({
    updateDisplayName: (value) => {
      dispatch(change(props.formNamespace, 'display_name', value));
    },
    updateCustomerKind: (value) => {
      dispatch(change(props.formNamespace, 'kind', value));
    },
    addAdditionalEmail: () => {
      dispatch(addAdditionalEmails(props.formNamespace));
    },
    addAdditionalPhone: () => {
      dispatch(addAdditionalPhones(props.formNamespace));
    },
  })
)
@withStyles(styles)
export default class ContactInfoFormContent extends PureComponent {
  componentDidUpdate(preProps, preState) {
    if (shouldUseDefaultDisplayName(preProps, this.props)) {
      this.props.updateDisplayName(this.props.defaultDisplayName);
    }
  }

  render() {
    const { classes } = this.props;
    const renderAdditionalEmails = ({ fields, meta: { error, submitFailed } }) => {
      return (
        <Grid item xs={12} md={6} className={!fields.length && classes.emptyPaddingCell}>
          {fields.map((email, index) => (
            <Grid key={index} container spacing={24}>
              <Grid item xs={11}>
                <FormTextField name={`${email}.email`} label="Email" fullWidth={true} />
              </Grid>
              <Grid item xs={1}>
                <IconButton color="delete" onClick={() => fields.remove(index)} className={classes.deleteRowStyle}>
                  <CancelIcon className={classes.deleteRowIcon} />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Grid>
      );
    };

    const renderAdditionalPhones = ({ fields, meta: { error, submitFailed } }) => {
      return (
        <Grid item xs={12} md={6} className={!fields.length && classes.emptyPaddingCell}>
          {fields.map((phone, index) => (
            <Grid key={index} container spacing={24}>
              <Grid item xs={6} sm={4}>
                <FormPhoneNumberField
                  useLatestMaterialUI={true}
                  name={`${phone}.phone_number`}
                  label="Additional phone"
                  fullWidth={true}
                />
              </Grid>
              <Grid item xs={5} sm={7} md={4}>
                <FormTextField name={`${phone}.notes`} label="Note" fullWidth={true} normalize="40Max" />
              </Grid>
              <Grid item xs={1}>
                <IconButton color="delete" onClick={() => fields.remove(index)} className={classes.deleteRowStyle}>
                  <CancelIcon className={classes.deleteRowIcon} />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Grid>
      );
    };

    let housecallUserChip = null;

    if (this.props.housecallUser) {
      housecallUserChip = <ConsumerAppChip />;
    }

    return (
      <div>
        <Grid container spacing={24}>
          <Hidden mdUp>
            <Grid item xs={6}>
              <SectionTitle name="Contact info" icon={<FaceIcon />} />
            </Grid>
            <Grid item xs={6}>
              {housecallUserChip}
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField
                id="customer-dialog-first-name"
                autoFocus
                name="first_name"
                label="First name"
                fullWidth={true}
                disabled={this.props.housecallUser}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField name="last_name" label="Last name" fullWidth={true} disabled={this.props.housecallUser} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormTextField name="display_name" label="Display name (shown on invoices)" fullWidth={true} />
            </Grid>
            <Hidden smUp>
              <Grid item xs={12} />
            </Hidden>
            <Grid item xs={12} sm={4}>
              <FormPhoneNumberField
                useLatestMaterialUI={true}
                name="mobile_number"
                label="Mobile phone"
                fullWidth={true}
                disabled={this.props.housecallUser}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormPhoneNumberField
                useLatestMaterialUI={true}
                name="home_number"
                label="Home phone"
                fullWidth={true}
                disabled={this.props.housecallUser}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormPhoneNumberField useLatestMaterialUI={true} name="work_number" label="Work phone" fullWidth={true} />
            </Grid>
            <FieldArray name="additional_phone_numbers" component={renderAdditionalPhones} />
            <Grid item xs={12}>
              <Button
                className={classes.addMoreButton}
                variant="outlined"
                color="primary"
                onClick={() => this.props.addAdditionalPhone()}
              >
                + Phone
              </Button>
            </Grid>
            <Hidden smUp>
              <Grid item xs={12} />
            </Hidden>
            <Grid item xs={12}>
              <FormTextField name="email" label="Email" fullWidth={true} disabled={this.props.housecallUser} />
            </Grid>
            <FieldArray name="additional_emails" component={renderAdditionalEmails} />
            <Grid item xs={12}>
              <Button
                className={classes.addMoreButton}
                variant="outlined"
                color="primary"
                onClick={() => this.props.addAdditionalEmail()}
              >
                + Email
              </Button>
            </Grid>
            <Hidden smUp>
              <Grid item xs={12} />
            </Hidden>
            <Grid item xs={12} sm={6}>
              <FormTextField name="company" label="Company" fullWidth={true} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormTextField name="job_title" label="Role" fullWidth={true} />
            </Grid>
            <Grid item container xs={12} sm={6}>
              <RadioGroup
                name="customer_kind"
                value={this.props.customerKind}
                onChange={(event, value) => {
                  this.props.updateCustomerKind(value);
                }}
              >
                <Grid container spacing={48}>
                  <Grid item xs={6}>
                    <FormControlLabel value="homeowner" control={<Radio color="primary" />} label="Homeowner" />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel value="business" control={<Radio color="primary" />} label="Business" />
                  </Grid>
                </Grid>
              </RadioGroup>
            </Grid>
            {this.props.customerKind === 'business' && (
              <Grid item xs={12} sm={6}>
                <HousecallThemeProvider>
                  <CustomerSubcontractCell />
                </HousecallThemeProvider>
              </Grid>
            )}
          </Hidden>
          <Hidden smDown>
            <Grid item sm={6}>
              <SectionTitle name="Contact info" icon={<FaceIcon />} />
            </Grid>
            <Grid item sm={6}>
              {housecallUserChip}
            </Grid>
            <Grid item sm={3}>
              <FormTextField
                id="customer-dialog-first-name"
                autoFocus
                name="first_name"
                label="First name"
                fullWidth={true}
                disabled={this.props.housecallUser}
              />
            </Grid>
            <Grid item sm={3}>
              <FormTextField name="last_name" label="Last name" fullWidth={true} disabled={this.props.housecallUser} />
            </Grid>
            <Grid item sm={2}>
              <FormPhoneNumberField
                useLatestMaterialUI={true}
                name="mobile_number"
                label="Mobile phone"
                fullWidth={true}
                disabled={this.props.housecallUser}
              />
            </Grid>
            <Grid item sm={3} className={classes.companyPosition}>
              <FormTextField name="company" label="Company" fullWidth={true} />
            </Grid>
            <Grid item sm={6}>
              <FormTextField name="display_name" label="Display name (shown on invoices)" fullWidth={true} />
            </Grid>
            <Grid item sm={2}>
              <FormPhoneNumberField
                useLatestMaterialUI={true}
                name="home_number"
                label="Home phone"
                fullWidth={true}
                disabled={this.props.housecallUser}
              />
            </Grid>
            <Grid item sm={3} className={classes.companyPosition}>
              <FormTextField name="job_title" label="Role" fullWidth={true} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormTextField name="email" label="Email" fullWidth={true} disabled={this.props.housecallUser} />
            </Grid>
            <Grid item sm={2}>
              <FormPhoneNumberField useLatestMaterialUI={true} name="work_number" label="Work phone" fullWidth={true} />
            </Grid>
            <Grid item sm={3} className={classes.companyPosition}>
              <RadioGroup
                name="customer_kind"
                value={this.props.customerKind}
                onChange={(event, value) => {
                  this.props.updateCustomerKind(value);
                }}
              >
                <FormControlLabel
                  value="homeowner"
                  control={<Radio className={classes.radioButton} color="primary" />}
                  label="Homeowner"
                />
                <FormControlLabel
                  value="business"
                  control={<Radio className={classes.radioButton} color="primary" />}
                  label="Business"
                />
              </RadioGroup>
            </Grid>
            <FieldArray name="additional_emails" component={renderAdditionalEmails} />
            <FieldArray name="additional_phone_numbers" component={renderAdditionalPhones} />
            <Grid item sm={6}>
              <Button
                className={classes.addMoreButton}
                variant="outlined"
                color="primary"
                onClick={() => this.props.addAdditionalEmail()}
              >
                + Email
              </Button>
            </Grid>
            <Grid item sm={2}>
              <Button
                className={classes.addMoreButton}
                variant="outlined"
                color="primary"
                onClick={() => this.props.addAdditionalPhone()}
              >
                + Phone
              </Button>
            </Grid>
            {this.props.customerKind === 'business' && (
              <Grid item sm={3} className={classes.companyPosition}>
                <HousecallThemeProvider>
                  <CustomerSubcontractCell />
                </HousecallThemeProvider>
              </Grid>
            )}
          </Hidden>
        </Grid>
      </div>
    );
  }
}
